import { produce } from 'immer';
import { createSelector } from 'reselect';
import { find } from 'lodash-es';
import * as api from '../../lib/api';
import { ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';

const INITIAL_STATE = {
  isLoading: false,
  error: null,
  fields: [],
};

const AsyncType = ScopedAsyncType('planting');

const SLICE = 'planting';
const LOAD_RAW_PLANTING_FOR_GROWER = AsyncType('LOAD_RAW_PLANTING_FOR_GROWER');
const LOAD_CLEAN_PLANTING_FOR_GROWER = AsyncType(
  'LOAD_CLEAN_PLANTING_FOR_GROWER',
);
export const SET_CLEAN_FIELD_APPROVAL = AsyncType('SET_CLEAN_FIELD_APPROVAL');

export const selectors = {
  selectedPlantingFields: createSelector(
    (state) => state[SLICE].fields,
    (state) => state.fields.selectedGrowerFields,
    (moduleFields, growerFields) =>
      growerFields?.length && moduleFields?.length
        ? growerFields.map((growerField) =>
            find(moduleFields, {
              aaManagedId: growerField.aaManagedId,
            }),
          )
        : [],
  ),
};

export const actions = {
  loadRawPlantingForGrower,
  loadCleanPlantingForGrower,
  setApprovedCleanField,
};

function loadRawPlantingForGrower(growerId, year) {
  return asyncAction(LOAD_RAW_PLANTING_FOR_GROWER, () =>
    api.getRawPlantingForGrower(growerId, year),
  );
}

function loadCleanPlantingForGrower(growerId, year) {
  return asyncAction(LOAD_CLEAN_PLANTING_FOR_GROWER, () =>
    api.getCleanPlantingForGrower(growerId, year),
  );
}

function setApprovedCleanField(aaid, year, approved) {
  return asyncAction(
    SET_CLEAN_FIELD_APPROVAL,
    () => api.putApproveCleanField(aaid, year, approved),
    {
      startPayload: { aaid, year, approved },
      failurePayload: { aaid, year, approved: !approved },
    },
  );
}

const reducerHandlers = {
  [LOAD_RAW_PLANTING_FOR_GROWER.start]: () => ({
    isLoading: true,
    error: null,
    fields: [],
  }),
  [LOAD_RAW_PLANTING_FOR_GROWER.complete]: (_, payload) => ({
    isLoading: false,
    error: null,
    fields: payload,
  }),
  [LOAD_RAW_PLANTING_FOR_GROWER.failure]: (_, payload) => ({
    isLoading: false,
    error: payload,
    fields: [],
  }),
  [LOAD_CLEAN_PLANTING_FOR_GROWER.start]: () => ({
    isLoading: true,
    error: null,
    cleanFields: [],
  }),
  [LOAD_CLEAN_PLANTING_FOR_GROWER.complete]: (_, payload) => ({
    isLoading: false,
    error: null,
    cleanFields: payload,
  }),
  [LOAD_CLEAN_PLANTING_FOR_GROWER.failure]: (_, payload) => ({
    isLoading: false,
    error: payload,
    cleanFields: [],
  }),
  [SET_CLEAN_FIELD_APPROVAL.start]: (state, payload) => {
    state.cleanFields.forEach((field) => {
      if (field.aaManagedId === payload.aaid)
        field.plantings[0].approved = payload.approved;
    });
  },
  [SET_CLEAN_FIELD_APPROVAL.failure]: (state, payload) => {
    state.cleanFields.forEach((field) => {
      if (field.aaManagedId === payload.aaid)
        field.plantings[0].approved = payload.approved;
    });
  },
};

export const reducers = {
  [SLICE]: (state = INITIAL_STATE, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
